<template>
  <Modal
    :title="modalTitle"
    :disabled="locked || !canSave"
    :saving="saving"
    :submit-btn-text="`Save ${programName}`"
    @hide-modal="onHideModal"
    @submit="handleSubmit"
  >
    <div
      ref="contentRef"
      class="spb-modal d-flex flex-column gap-3 min-h-[320px]"
    >
      <p class="text-muted mb-0 spb-modal__intro" data-cy="quote-total">
        {{ targetLabel }}: <strong>{{ formattedTotal }}</strong> —
        <template v-if="locked">
          {{ programName }} is locked because the invoice is finalized.
        </template>
        <template v-else-if="paidRowsLocked">
          set remaining {{ intervalName.toLowerCase() }} amounts below.
        </template>
        <template v-else>
          set {{ intervalName.toLowerCase() }} amounts below. The
          {{ programName }} locks when the invoice is finalized.
        </template>
      </p>

      <div
        v-if="paidRowsLocked && !locked"
        class="spb-modal__historical-note"
        data-cy="historical-installments-note"
      >
        <i class="fas fa-lock mr-2" />
        <span>
          {{ lockedInstallmentCount }}
          historical
          {{
            lockedInstallmentCount === 1
              ? "installment is locked and keeps its"
              : "installments are locked and keep their"
          }}
          share of the {{ programName.toLowerCase() }} total.
        </span>
      </div>

      <div
        v-if="!existingPlan"
        class="d-flex align-items-center gap-2"
        data-cy="template-picker"
      >
        <label class="font-semibold mb-0" for="spb-template-select"
          >Template:</label
        >
        <select
          id="spb-template-select"
          v-model.number="selectedTemplateId"
          class="form-control form-control-sm"
          style="min-width: 320px"
          :disabled="templatesLoading || !templates.length"
          data-cy="template-select"
          @change="seedFromTemplate"
        >
          <option v-if="templatesLoading" :value="null">Loading…</option>
          <option v-else-if="!templates.length" :value="null">
            No templates available
          </option>
          <option v-for="t in templates" :key="t.id" :value="t.id">
            {{ templateLabel(t) }}
          </option>
        </select>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-2">
        <h6 class="font-semibold mb-0">{{ intervalNamePlural }}</h6>
        <div class="spb-modal__toggle" role="group" data-cy="split-mode-toggle">
          <button
            type="button"
            class="spb-modal__toggle-btn"
            :class="{
              'spb-modal__toggle-btn--active': splitMode === 'percentage'
            }"
            :disabled="locked || paidRowsLocked"
            data-cy="split-percentage"
            @click="setSplitMode('percentage')"
          >
            By Percentage
          </button>
          <button
            type="button"
            class="spb-modal__toggle-btn"
            :class="{ 'spb-modal__toggle-btn--active': splitMode === 'amount' }"
            :disabled="locked || paidRowsLocked"
            data-cy="split-amount"
            @click="setSplitMode('amount')"
          >
            By Amount
          </button>
        </div>
      </div>

      <div class="d-flex align-items-center gap-2 text-muted small">
        <span style="width: 100%">Label</span>
        <span style="width: 140px; flex: 0 0 140px; text-align: left">{{
          splitMode === "percentage" ? "%" : "Amount"
        }}</span>
        <span style="width: 90px; flex: 0 0 90px; text-align: left">{{
          splitMode === "percentage" ? "= $" : "= %"
        }}</span>
        <span style="width: 160px; flex: 0 0 160px; text-align: left"
          >Due date</span
        >
        <span style="width: 32px"></span>
      </div>

      <PaymentScheduleInstallmentRow
        v-for="(installment, idx) in visibleInstallments"
        :key="installment.id ?? `new-${idx}`"
        :installment="installment"
        :split-mode="splitMode"
        :removable="
          !locked &&
          !isPaidInstallment(installment) &&
          editableInstallments.length > 1
        "
        :error="rowError(installment)"
        :due-date-error="dueDateError(installment)"
        :disabled="templatesLoading || locked || isPaidInstallment(installment)"
        :locked="isPaidInstallment(installment)"
        :min-date="rowMinDate(idx)"
        data-cy="installment-row"
        @remove="removeRow(installment)"
        @update:label="(v) => updateLabel(installment, v)"
        @update:value="(v) => updateValue(installment, v)"
        @update:dueDate="(v) => updateDueDate(installment, v)"
      />

      <ItemAdder
        v-if="!locked"
        slim
        transparent
        :text="`Add ${intervalName}`"
        :disabled="templatesLoading || noEditableInstallments"
        data-cy="add-installment"
        @click="addRow"
      />

      <div
        v-if="!locked"
        class="spb-modal__summary mt-3"
        data-cy="allocation-summary"
      >
        <div
          class="d-flex justify-content-between align-items-center mb-1 small"
        >
          <div data-cy="allocated-display">
            {{ allocationLabel }}: <strong>{{ allocatedDisplay }}</strong>
          </div>
          <div
            :class="fullyAllocated ? 'text-success' : 'text-yellow-700'"
            data-cy="allocation-status"
          >
            <span v-if="fullyAllocated">✓ Fully allocated</span>
            <span v-else>{{ remainingDisplay }}</span>
          </div>
        </div>
        <div
          class="progress spb-modal__progress"
          role="progressbar"
          :aria-valuenow="progressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="progress-bar"
            :class="fullyAllocated ? 'bg-success' : 'bg-warning'"
            :style="{ width: progressPercent + '%' }"
          />
        </div>
        <div
          v-if="!fullyAllocated && unallocatedWarning"
          class="text-danger small mt-2"
          data-cy="allocation-warning"
        >
          <i class="fas fa-exclamation-triangle mr-1" />{{ unallocatedWarning }}
        </div>
      </div>
    </div>
    <template #footer>
      <div
        class="modal-footer d-flex justify-content-between align-items-center spb-modal__footer"
      >
        <div
          v-if="dueDateBlockMessage"
          class="text-danger small mb-0 mr-3"
          role="alert"
          data-cy="due-date-blocked-message"
        >
          <i class="fas fa-exclamation-triangle mr-1" />{{
            dueDateBlockMessage
          }}
        </div>
        <div v-else class="flex-grow-1"></div>
        <div class="d-flex align-items-center">
          <SeraButton
            type="secondary"
            text="Cancel"
            data-cy="modal-cancel-btn"
            class="mr-2"
            @click="onHideModal"
          />
          <SeraButton
            :disabled="locked || !canSave || saving"
            :saving="saving"
            data-cy="modal-submit-btn"
            @click="handleSubmit"
          >
            <span v-if="!saving">Save {{ programName }}</span>
            <Spinner v-else message="Saving..." />
          </SeraButton>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { trackEvent } from "@/lib/pendo";

import SeraButton from "@/components/Button.vue";
import ItemAdder from "@/components/ItemAdder.vue";
import Modal from "@/components/Modal.vue";
import Spinner from "@/components/Spinner.vue";
import { useProgressBillingLabels } from "@/composables/useProgressBillingLabels";
import { dollars } from "@/mixins";
import PaymentScheduleInstallmentRow from "./PaymentScheduleInstallmentRow.vue";

const { programName, intervalName, intervalNamePlural } =
  useProgressBillingLabels();

interface TemplateInstallment {
  id?: number;
  label: string;
  percentage: number;
  position: number;
}

interface ProgressBillingTemplate {
  id?: number;
  name: string;
  description?: string;
  is_active: boolean;
  system_default: boolean;
  installments: TemplateInstallment[];
}

type SplitMode = "percentage" | "amount";

interface Installment {
  id?: number;
  label: string;
  label_overridden?: boolean;
  value: number;
  calculated_opposite_value: number;
  amount_cents?: number;
  paid_at?: string | null;
  position: number;
  due_date?: string | null;
  _destroy?: boolean;
}

interface JobPaymentSchedule {
  id: number;
  job_id: number;
  split_mode: SplitMode;
  status: string;
  installments: Installment[];
}

interface JobPaymentScheduleResponse {
  schedule: JobPaymentSchedule | null;
}

const props = withDefaults(
  defineProps<{
    jobId: number;
    // The dollar amount this plan divides up. Not the live balance: rows the
    // customer has already paid still hold their share of it.
    planTotalCents: number;
    existingPlan?: JobPaymentSchedule | null;
    planLocked?: boolean;
  }>(),
  {
    existingPlan: null,
    planLocked: false
  }
);

const locked = computed(() => props.planLocked);

const emit = defineEmits<{
  "hide-modal": [];
  saved: [response: JobPaymentScheduleResponse];
  "needs-refresh": [];
}>();

const store = useStore();

const saving = ref(false);
// No explicit reset needed: the parent renders this modal with v-if
// (InvoiceActionsContextMenu), so it is recreated on each open and this starts
// false every time. If a caller ever keeps it alive, reset this on open.
const saveAttempted = ref(false);
const contentRef = ref<HTMLElement | null>(null);
const defaultNetDays = ref(30);
// eslint-disable-next-line prefer-const
let saveCompleted = false;
const initialSplitMode: SplitMode =
  props.existingPlan?.split_mode ?? "percentage";
const splitMode = ref<SplitMode>(initialSplitMode);

const installments = ref<Installment[]>(
  props.existingPlan
    ? coerceInstallments(props.existingPlan, initialSplitMode)
    : defaultInstallments(props.planTotalCents)
);
function coerceInstallments(
  plan: JobPaymentSchedule,
  targetMode: SplitMode
): Installment[] {
  const copies = plan.installments.map((i) => ({ ...i }));
  if (plan.split_mode === targetMode) return copies;
  return copies.map((inst) => ({
    ...inst,
    value: Number(inst.calculated_opposite_value),
    calculated_opposite_value: Number(inst.value)
  }));
}

const templates = ref<ProgressBillingTemplate[]>([]);
const templatesLoading = ref(false);
const selectedTemplateId = ref<number | null>(null);
const userTouched = ref(false);

onMounted(async () => {
  if (props.existingPlan) return;
  templatesLoading.value = true;
  try {
    const [list, config] = await Promise.all([
      store.dispatch("fetchProgressBillingSchedules"),
      store
        .dispatch("fetchProgressBillingConfig")
        .catch(() => null as { default_schedule_id: number | null } | null)
    ]);
    defaultNetDays.value = config?.default_net_days ?? 30;
    templates.value = (list ?? []).filter(
      (t: ProgressBillingTemplate) => t.is_active
    );
    const fallback = templates.value[0]?.id ?? null;
    selectedTemplateId.value = config?.default_schedule_id ?? fallback;
    if (selectedTemplateId.value && !userTouched.value) seedFromTemplate();
    applyDefaultDueDates();
  } finally {
    templatesLoading.value = false;
  }
});

function templateLabel(t: ProgressBillingTemplate): string {
  const summary = t.installments.map((i) => `${i.percentage}%`).join(" / ");
  return summary ? `${t.name} (${summary})` : t.name;
}

function seedFromTemplate(): void {
  const tpl = templates.value.find((t) => t.id === selectedTemplateId.value);
  if (!tpl || !tpl.installments.length) return;
  const existingDueDates = visibleInstallments.value.map((i) => i.due_date);
  splitMode.value = "percentage";
  installments.value = tpl.installments
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((tplInst, idx) => ({
      label: tplInst.label,
      label_overridden: false,
      value: Math.round(Number(tplInst.percentage)),
      calculated_opposite_value: 0,
      position: idx,
      due_date: existingDueDates[idx] ?? null
    }));
  normalizeIntegerPercents();
  recalcAllOpposites();
  applyDefaultDueDates();
}

const modalTitle = computed(() => {
  if (locked.value) return `View ${programName.value}`;
  return props.existingPlan
    ? `Edit ${programName.value}`
    : `Configure ${programName.value}`;
});

const visibleInstallments = computed(() =>
  installments.value.filter((i) => !i._destroy)
);

function isPaidInstallment(row: Installment): boolean {
  return Boolean(row.paid_at);
}

const editableInstallments = computed(() =>
  visibleInstallments.value.filter((i) => !isPaidInstallment(i))
);

const paidRowsLocked = computed(() =>
  visibleInstallments.value.some((i) => isPaidInstallment(i))
);

const lockedInstallmentCount = computed(
  () => visibleInstallments.value.filter((i) => isPaidInstallment(i)).length
);

// A paid row's dollars are frozen at what was collected. They still count
// toward the plan total — they are just no longer editable.
function paidDollarsFor(row: Installment): number {
  if (row.amount_cents != null) return Number(row.amount_cents);
  return Number(
    splitMode.value === "amount" ? row.value : row.calculated_opposite_value
  );
}

const lockedCents = computed(() =>
  visibleInstallments.value
    .filter(isPaidInstallment)
    .reduce((sum, row) => sum + paidDollarsFor(row), 0)
);

const lockedPercent = computed(() =>
  visibleInstallments.value
    .filter(isPaidInstallment)
    .reduce((sum, row) => sum + Number(row.value || 0), 0)
);

// What the open rows share. Only the rounding residual is scoped to this —
// percentages stay measured against the full plan total.
const openTotalCents = computed(() =>
  Math.max(0, props.planTotalCents - lockedCents.value)
);

const formattedTotal = computed(() => dollars(props.planTotalCents));

const targetLabel = computed(() => `${programName.value} total`);

const allocationLabel = computed(() => "Allocated");

const noEditableInstallments = computed(
  () => paidRowsLocked.value && editableInstallments.value.length === 0
);

if (props.existingPlan && !props.planLocked) {
  // Legacy plans may have been saved with fractional percents; lock them to
  // whole integers on load so the editor matches the current behavior.
  if (splitMode.value === "percentage") normalizeIntegerPercents();
  if (splitMode.value === "amount" && paidRowsLocked.value) {
    rescaleAmountsTo(openTotalCents.value, editableAmountTotal());
  }
  recalcAllOpposites();
}

// Every living row counts, paid included — measuring only the editable rows
// is what let a 60/40 plan reconcile as 60/100.
const totalAllocated = computed(() =>
  visibleInstallments.value.reduce((sum, i) => sum + Number(i.value || 0), 0)
);

const target = computed(() =>
  splitMode.value === "percentage"
    ? visibleInstallments.value.length > 0
      ? 100
      : 0
    : props.planTotalCents
);

const fullyAllocated = computed(
  () => Math.abs(target.value - totalAllocated.value) < 0.001
);

const hasValueError = computed(() =>
  visibleInstallments.value.some((row) => rowError(row) !== null)
);

// Missing due dates do not gate the button — the technician can tap Save and
// get row-level feedback (TIDE-1635), rather than facing a silent grayed-out
// button. Allocation and value errors still keep the button disabled.
const missingDueDate = computed(
  () => !locked.value && editableInstallments.value.some((row) => !row.due_date)
);

const canSave = computed(
  () =>
    fullyAllocated.value &&
    !hasValueError.value &&
    !noEditableInstallments.value
);

const dueDateBlockMessage = computed(() =>
  saveAttempted.value && missingDueDate.value
    ? `Add a due date to each highlighted ${intervalName.value.toLowerCase()} to save.`
    : ""
);

const progressPercent = computed(() => {
  if (target.value <= 0) return 0;
  const ratio = (totalAllocated.value / target.value) * 100;
  return Math.max(0, Math.min(100, ratio));
});

const allocatedDisplay = computed(() => {
  if (splitMode.value === "percentage") {
    return `${formatPercent(totalAllocated.value)}%`;
  }
  return dollars(totalAllocated.value);
});

const remainingDisplay = computed(() => {
  const gap = target.value - totalAllocated.value;
  if (Math.abs(gap) < 0.001) return "";
  if (gap > 0) {
    return splitMode.value === "percentage"
      ? `${formatPercent(gap)}% remaining`
      : `${dollars(gap)} remaining`;
  }
  return splitMode.value === "percentage"
    ? `${formatPercent(-gap)}% over`
    : `${dollars(-gap)} over`;
});

const unallocatedWarning = computed(() => {
  const gap = target.value - totalAllocated.value;
  if (Math.abs(gap) < 0.001) return "";
  const sign = gap > 0 ? "" : "over by ";
  const absGap = Math.abs(gap);
  const pct =
    splitMode.value === "percentage"
      ? absGap
      : props.planTotalCents > 0
      ? (absGap / props.planTotalCents) * 100
      : 0;
  const cents =
    splitMode.value === "percentage"
      ? (absGap / 100) * props.planTotalCents
      : absGap;
  const verb =
    gap > 0 ? "still unallocated." : `over the ${programName.value} total.`;
  return `${sign}${formatPercent(pct)}% (${dollars(cents)}) ${verb}`;
});

function formatPercent(n: number): string {
  const tenth = Math.round(n * 10) / 10;
  if (Math.abs(n - tenth) < 0.0001) return tenth.toFixed(1);
  const fixed = n.toFixed(3).replace(/\.?0+$/, "");
  return /\./.test(fixed) ? fixed : `${fixed}.0`;
}

function rowError(row: Installment): string | null {
  if (locked.value) return null;
  if (isPaidInstallment(row)) return null;
  const v = Number(row.value);
  if (Number.isNaN(v) || v < 0) return "Value cannot be negative";
  if (splitMode.value === "percentage" && v > 100) {
    return "Cannot exceed 100%";
  }
  if (splitMode.value === "amount" && v > props.planTotalCents) {
    return `Exceeds the ${programName.value.toLowerCase()} total`;
  }
  if (!fullyAllocated.value && v === 0) return "Enter a value";
  return null;
}

// Required-field feedback surfaces only after a save attempt, so empty dates
// don't flag a row the technician hasn't tried to submit yet.
function dueDateError(row: Installment): string | null {
  if (locked.value || !saveAttempted.value) return null;
  if (isPaidInstallment(row)) return null;
  if (!row.due_date) return "Due date is required";
  return null;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function nextDayIso(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return next.toISOString().slice(0, 10);
}

function addDaysIso(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const result = new Date(Date.UTC(y, m - 1, d + days));
  return result.toISOString().slice(0, 10);
}

function applyDefaultDueDates(): void {
  const today = todayIso();
  editableInstallments.value.forEach((inst, idx) => {
    if (!inst.due_date) {
      const previous = editableInstallments.value[idx - 1]?.due_date;
      inst.due_date = previous
        ? addDaysIso(previous, defaultNetDays.value)
        : addDaysIso(today, idx * defaultNetDays.value);
    }
  });
}

function rowMinDate(idx: number): string {
  let floor = todayIso();
  for (let i = idx - 1; i >= 0; i--) {
    const earlier = visibleInstallments.value[i].due_date;
    if (!earlier) continue;
    const dayAfter = nextDayIso(earlier);
    if (dayAfter > floor) floor = dayAfter;
    break;
  }
  return floor;
}

function defaultInstallments(totalCents: number): Installment[] {
  const half = Math.round(totalCents / 2);
  return [
    {
      label: "Deposit",
      value: 50,
      calculated_opposite_value: half,
      position: 0,
      label_overridden: false,
      due_date: null
    },
    {
      label: "Final",
      value: 50,
      calculated_opposite_value: totalCents - half,
      position: 1,
      label_overridden: false,
      due_date: null
    }
  ];
}

function autoLabel(index: number, total: number): string {
  if (total <= 1) return "Deposit";
  if (index === 0) return "Deposit";
  if (index === total - 1) return "Final";
  return `${intervalName.value} ${index + 1}`;
}

function rewriteAutoLabels(): void {
  const living = installments.value.filter((i) => !i._destroy);
  const n = living.length;
  living.forEach((inst, idx) => {
    if (!isPaidInstallment(inst) && !inst.label_overridden) {
      inst.label = autoLabel(idx, n);
    }
    inst.position = idx;
  });
}

function addRow(): void {
  if (noEditableInstallments.value) return;
  userTouched.value = true;
  const visible = visibleInstallments.value;
  const lastDate =
    visible.length > 0 ? visible[visible.length - 1].due_date : null;
  const newDueDate = lastDate
    ? addDaysIso(lastDate, defaultNetDays.value)
    : addDaysIso(todayIso(), installments.value.length * defaultNetDays.value);

  installments.value.push({
    label: "",
    value: 0,
    calculated_opposite_value: 0,
    position: installments.value.length,
    label_overridden: false,
    due_date: newDueDate
  });
  rewriteAutoLabels();
  recalcAllOpposites();
}

function removeRow(row: Installment): void {
  if (isPaidInstallment(row)) return;
  userTouched.value = true;
  if (row.id) {
    row._destroy = true;
    installments.value = [...installments.value];
  } else {
    const idx = installments.value.indexOf(row);
    if (idx !== -1) installments.value.splice(idx, 1);
  }
  rewriteAutoLabels();
  recalcAllOpposites();
}

function updateLabel(row: Installment, label: string): void {
  if (isPaidInstallment(row)) return;
  userTouched.value = true;
  row.label = label;
  row.label_overridden = label.trim().length > 0;
}

function updateValue(row: Installment, value: number): void {
  if (isPaidInstallment(row)) return;
  userTouched.value = true;
  row.value = value;
  // Recompute the whole set so the last row keeps absorbing the dollar
  // residual in percentage mode (in amount mode this just refreshes each
  // row's derived percentage).
  recalcAllOpposites();
}

function updateDueDate(row: Installment, due_date: string | null): void {
  if (isPaidInstallment(row)) return;
  userTouched.value = true;
  row.due_date = due_date;
}

// Amount mode: derive this row's fractional percentage from its dollar value.
// The percentage display intentionally stays fractional (e.g. 50.01%).
function recalcOpposite(row: Installment): void {
  if (props.planTotalCents > 0) {
    row.calculated_opposite_value = Number(
      ((Number(row.value) / props.planTotalCents) * 100).toFixed(2)
    );
  } else {
    row.calculated_opposite_value = 0;
  }
}

// Percentage mode: percents are whole-integer source of truth. Derive each
// row's dollars, then route the residual cents to the last living row so the
// dollars sum exactly to the balance — mirroring the backend's
// InvoicePresenter#compute_dollar_values. The last row's percent is never
// recomputed from its adjusted dollars (that recompute is what produced the
// 50.01 / 49.99 artifacts this fixes).
function recalcPercentageDollars(): void {
  const editable = editableInstallments.value;
  if (!editable.length) return;
  let allocated = 0;
  editable.forEach((row, idx) => {
    if (idx < editable.length - 1) {
      row.calculated_opposite_value = Math.round(
        (Number(row.value) / 100) * props.planTotalCents
      );
      allocated += row.calculated_opposite_value;
    } else {
      // Only the dollars the paid rows have not already claimed are left for
      // the last editable row to absorb.
      row.calculated_opposite_value = Math.max(
        0,
        openTotalCents.value - allocated
      );
    }
  });
}

// Force percents to whole integers that still sum to 100 by giving the last
// living row whatever the earlier rows leave over.
function normalizeIntegerPercents(): void {
  const editable = editableInstallments.value;
  if (!editable.length) return;
  let allocated = 0;
  editable.forEach((row, idx) => {
    if (idx < editable.length - 1) {
      row.value = Math.max(0, Math.round(Number(row.value)));
      allocated += row.value;
    } else {
      // Reconciling to a bare 100 hands this row the paid rows' share too,
      // turning a 60/40 plan's final draw into 100% of the whole job.
      row.value = Math.max(0, 100 - lockedPercent.value - allocated);
    }
  });
}

function recalcAllOpposites(): void {
  if (splitMode.value === "percentage") {
    recalcPercentageDollars();
  } else {
    editableInstallments.value.forEach((row) => recalcOpposite(row));
  }
}

function setSplitMode(mode: SplitMode): void {
  if (mode === splitMode.value) return;
  if (paidRowsLocked.value) return;
  userTouched.value = true;
  splitMode.value = mode;
  installments.value = installments.value.map((inst) => ({
    ...inst,
    value: inst.calculated_opposite_value,
    calculated_opposite_value: inst.value
  }));
  if (mode === "percentage") {
    // Swapping dollars→percents yields fractional values; lock them to whole
    // integers and let the last row reconcile the dollar residual.
    normalizeIntegerPercents();
  }
  recalcAllOpposites();
  trackEvent("spb_split_mode_changed", {
    job_id: props.jobId,
    split_mode: mode
  });
}

watch(
  () => props.planTotalCents,
  (newTotal, oldTotal) => {
    if (
      splitMode.value === "amount" &&
      oldTotal > 0 &&
      newTotal > 0 &&
      newTotal !== oldTotal
    ) {
      // Paid rows keep their dollars through a total change, so only the open
      // portion rescales.
      rescaleAmountsTo(
        newTotal - lockedCents.value,
        oldTotal - lockedCents.value
      );
    }
    recalcAllOpposites();
  }
);

function rescaleAmountsTo(newTotal: number, oldTotal: number): void {
  const living = editableInstallments.value;
  if (!living.length) return;
  if (oldTotal <= 0) return;
  const ratio = newTotal / oldTotal;
  let allocated = 0;
  living.forEach((row, idx) => {
    if (idx < living.length - 1) {
      row.value = Math.round(Number(row.value) * ratio);
      allocated += row.value;
    } else {
      row.value = Math.max(0, newTotal - allocated);
    }
  });
}

function editableAmountTotal(): number {
  return editableInstallments.value.reduce(
    (sum, row) => sum + Number(row.value || 0),
    0
  );
}

function onHideModal(): void {
  if (!saveCompleted) {
    trackEvent("spb_modal_abandoned", {
      job_id: props.jobId,
      is_new_plan: !props.existingPlan
    });
  }
  emit("hide-modal");
}

async function focusFirstMissingDueDate(): Promise<void> {
  await nextTick();
  // Target the first empty date input directly rather than the row's is-invalid
  // class, so focus does not silently break if the error-styling binding changes.
  const inputs = contentRef.value?.querySelectorAll<HTMLInputElement>(
    "[data-cy='installment-due-date']"
  );
  const el = inputs && Array.from(inputs).find((input) => !input.value);
  el?.focus();
  el?.scrollIntoView?.({ block: "nearest" });
}

async function handleSubmit(): Promise<void> {
  if (locked.value || !canSave.value || saving.value) return;

  saveAttempted.value = true;
  if (missingDueDate.value) {
    await focusFirstMissingDueDate();
    return;
  }

  saving.value = true;
  try {
    const payload = {
      split_mode: splitMode.value,
      expected_balance_cents: props.planTotalCents,
      installments_attributes: installments.value.map((i) => ({
        id: i.id,
        label: i.label,
        label_overridden: i.label_overridden ?? false,
        value: i.value,
        calculated_opposite_value: i.calculated_opposite_value,
        position: i.position,
        due_date: i.due_date,
        _destroy: i._destroy
      }))
    };

    const action = props.existingPlan
      ? "updateJobPaymentSchedule"
      : "createJobPaymentSchedule";
    const response = await store.dispatch(action, {
      jobId: props.jobId,
      ...payload
    });
    saveCompleted = true;
    const isNewPlan = !props.existingPlan;
    if (isNewPlan) {
      trackEvent("spb_enabled", {
        job_id: props.jobId
      });
    }
    trackEvent("spb_plan_saved", {
      job_id: props.jobId,
      is_new_plan: isNewPlan,
      split_mode: splitMode.value,
      installments_count: visibleInstallments.value.length,
      due_dates_set: visibleInstallments.value.filter((i) => i.due_date).length,
      due_dates_blank: visibleInstallments.value.filter((i) => !i.due_date)
        .length
    });
    window.notify?.success(`${programName.value} saved successfully!`);
    emit("saved", response);
    emit("hide-modal");
  } catch (err: any) {
    const status = err?.response?.status;
    const serverMessage = extractErrorMessage(err);
    if (status === 404) {
      // Other 404s (e.g. a missing job) carry raw record-lookup text, so only
      // a progress billing gate's message is shown.
      const gateCoded = String(
        err?.response?.data?.error_code ?? ""
      ).startsWith("progress_billing_");
      window.notify?.error?.(
        (gateCoded ? serverMessage : null) ??
          "Payment schedule isn't available for this job."
      );
    } else if (status === 409) {
      window.notify?.error?.(
        serverMessage ??
          "Invoice balance has changed since you opened the modal — please try again."
      );
      emit("needs-refresh");
      emit("hide-modal");
      return;
    } else {
      window.notify?.error?.(
        serverMessage ??
          `Failed to save ${programName.value}. Please try again.`
      );
    }
    window.Sentry?.captureException(err);
    // eslint-disable-next-line no-console
    console.error("Failed to save payment schedule", err);
  } finally {
    saving.value = false;
  }
}

function extractErrorMessage(err: any): string | null {
  const data = err?.response?.data;
  if (!data) return null;
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  const baseErrors = data.errors?.base;
  if (Array.isArray(baseErrors) && baseErrors.length) return baseErrors[0];
  return null;
}
</script>

<style lang="scss" scoped>
.spb-modal__intro {
  font-size: 0.95rem;
  line-height: 1.4;
}

.spb-modal__historical-note {
  align-items: center;
  background: #f6f8fb;
  border: 1px solid #d8dee8;
  border-radius: 6px;
  color: #5f6b7a;
  display: flex;
  font-size: 0.875rem;
  line-height: 1.35;
  padding: 0.75rem 0.875rem;
}

.spb-modal__footer {
  border-top: 1px solid var(--light-color, #e9ecef);
  padding-top: 1rem;
}

.spb-modal__summary {
  border-top: 1px solid var(--light-color, #e9ecef);
  padding-top: 0.75rem;
}

.spb-modal__progress {
  height: 8px;
  border-radius: 4px;
}

.spb-modal__toggle {
  display: inline-flex;
  border: 1px solid var(--primary-color, #2563eb);
  border-radius: 6px;
  overflow: hidden;
  background-color: #fff;
}

.spb-modal__toggle > .spb-modal__toggle-btn + .spb-modal__toggle-btn {
  border-left: 1px solid var(--primary-color, #2563eb);
}

.spb-modal__toggle-btn {
  padding: 0.375rem 0.875rem;
  border: 0;
  background-color: transparent;
  color: var(--primary-color, #2563eb);
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover:not(.spb-modal__toggle-btn--active):not(:disabled) {
    background-color: rgba(37, 99, 235, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &--active {
    background-color: var(--primary-color, #2563eb);
    color: #fff;
  }
}
</style>
